import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "@services/auth.service";
import { ToastService } from "@services/toast.service";
import { Messages } from "@config/messages";
import { catchError, throwError } from "rxjs";

/** True only when 401 means the login JWT is invalid/expired — not resource-level access denied. */
function shouldLogoutOn401(err: HttpErrorResponse): boolean {
  const body = err.error;
  if (!body || typeof body !== "object") {
    return true;
  }

  const rules =
    (body as { FailRules?: { Msg?: string; Code?: string }[] }).FailRules ??
    (body as { failRules?: { Msg?: string; msg?: string; Code?: string; code?: string }[] })
      .failRules;

  if (!Array.isArray(rules) || !rules.length) {
    return true;
  }

  const text = [
    rules[0]?.Msg,
    (rules[0] as { msg?: string })?.msg,
    rules[0]?.Code,
    (rules[0] as { code?: string })?.code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    text.includes("token expired") ||
    text.includes("session expired") ||
    text.includes("invalid token") ||
    text.includes("jwt") ||
    text.includes("not authenticated") ||
    text.includes("authentication failed") ||
    text.includes("unauthorized user")
  );
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toast = inject(ToastService);
  const token = authService.getAuthTokenFromLocalStorage();

  const authReq = token
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    : req;

  return next(authReq).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        authService.getAuthTokenFromLocalStorage() &&
        shouldLogoutOn401(err)
      ) {
        toast.showWarning(Messages.MSG_SESSION_EXPIRED);
        authService.afterLogoutSuccess();
      }
      return throwError(() => err);
    })
  );
};
